import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import {
  APP_ENV,
  SOCKET_SERVER_URL,
  SOCKET_SERVER_URLS,
  WEBSOCKET_ENABLED_ENVS,
} from "static/constants";
import getToken from "utils/getToken";

const sockets = new Map();

/**
 * Connects to a socket.io namespace (eg SOCKET_SERVER_NAMESPACES) for the
 * current user, returning the shared socket once connected. Multiple hooks
 * on the same namespace + user reuse one connection (ref-counted in `sockets`),
 * which is closed once the last consumer unmounts.
 */
const useSocket = (namespace, { autoConnectOnMount = true } = {}) => {
  const socketUrl = SOCKET_SERVER_URLS[namespace] || SOCKET_SERVER_URL;

  const isEnabled =
    WEBSOCKET_ENABLED_ENVS.includes(APP_ENV) && Boolean(socketUrl);

  const token = getToken();
  const userId = localStorage.getItem("userId");
  const key = `${namespace}:${socketUrl}:${userId}`;

  const entryRef = useRef(null);

  if (isEnabled && !entryRef.current) {
    let entry = sockets.get(key);

    if (!entry) {
      const socket = io(socketUrl, { autoConnect: false });

      socket.isReady = false;
      socket.on("socket:ready", () => {
        socket.isReady = true;
      });
      socket.on("disconnect", () => {
        socket.isReady = false;
      });

      entry = {
        socket,
        refs: 0,
      };

      sockets.set(key, entry);
    }

    entryRef.current = entry;
  }

  useEffect(() => {
    if (entryRef.current && token) {
      entryRef.current.socket.auth = { token };
    }
  }, [token]);

  useEffect(() => {
    if (!isEnabled) {
      console.warn(
        "[WebSocket] Disabled: environment or socket url not configured"
      );
      return;
    }

    const entry = entryRef.current;
    if (!entry || !token) return;

    entry.refs += 1;

    if (autoConnectOnMount && !entry.socket.connected) {
      entry.socket.connect();
    }

    return () => {
      entry.refs -= 1;

      if (entry.refs === 0) {
        entry.socket.disconnect();
        sockets.delete(key);
      }
    };
  }, [isEnabled, autoConnectOnMount, key]);

  return entryRef.current?.socket ?? null;
};

export default useSocket;
